// BeneAI REST client — BeneAI-01.
//
// Thin wrappers around the four endpoints in
// ``backend/agents_runtime/beneai_urls.py``. Cookie-session auth +
// ``X-Entity-Id`` header ride for free through ``api`` (set by
// ``AuthProvider`` once the user picks a brokerage).

import { api } from "@/api/client";

import type {
  BeneAIConfirmationResolutionResponse,
  BeneAIConversationDetail,
  BeneAIConversationsList,
  BeneAINewConversation,
  BeneAISendMessageInput,
  BeneAISendMessageResponse,
} from "./types";


const BASE = "/api/v1/beneai";


export function listConversations(opts?: {
  signal?: AbortSignal;
}): Promise<BeneAIConversationsList> {
  return api.get<BeneAIConversationsList>(`${BASE}/conversations`, opts);
}


export function createConversation(opts?: {
  signal?: AbortSignal;
}): Promise<BeneAINewConversation> {
  return api.post<BeneAINewConversation>(
    `${BASE}/conversations`, {}, opts,
  );
}


export function getConversation(
  conversationId: string,
  opts?: { signal?: AbortSignal },
): Promise<BeneAIConversationDetail> {
  return api.get<BeneAIConversationDetail>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}`,
    opts,
  );
}


/** Soft-delete a conversation: server moves the session to
 *  ``ENDED`` so it drops out of the recents list. Messages / tool
 *  calls / receipts stay in place for audit + recall. */
export function deleteConversation(
  conversationId: string,
  opts?: { signal?: AbortSignal },
): Promise<{ detail: string }> {
  return api.delete<{ detail: string }>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}`,
    opts,
  );
}


export function sendMessage(
  conversationId: string | null,
  input: BeneAISendMessageInput,
  opts?: { signal?: AbortSignal },
): Promise<BeneAISendMessageResponse> {
  // The backend accepts the literal ``"new"`` sentinel to mean "create
  // a session on this message" so the FE can render an empty drawer
  // immediately without minting an empty session every open.
  const target = conversationId ?? "new";
  return api.post<BeneAISendMessageResponse>(
    `${BASE}/conversations/${encodeURIComponent(target)}/messages`,
    input,
    opts,
  );
}


// ── BeneAI-02 confirmation flow ──────────────────────────────────────────


/**
 * Confirm a pending mutating tool call. The backend executes the
 * stored ``input_json`` against the caller's ``request.entity`` /
 * ``request.user`` (model-supplied entity is ignored) and returns
 * the resulting receipt message.
 */
export function confirmConfirmation(
  confirmationId: string,
  opts?: { signal?: AbortSignal },
): Promise<BeneAIConfirmationResolutionResponse> {
  return api.post<BeneAIConfirmationResolutionResponse>(
    `${BASE}/confirmations/${encodeURIComponent(confirmationId)}/confirm`,
    {},
    opts,
  );
}


/** Cancel a pending mutating tool call. */
export function cancelConfirmation(
  confirmationId: string,
  opts?: { signal?: AbortSignal },
): Promise<BeneAIConfirmationResolutionResponse> {
  return api.post<BeneAIConfirmationResolutionResponse>(
    `${BASE}/confirmations/${encodeURIComponent(confirmationId)}/cancel`,
    {},
    opts,
  );
}
